import { useCallback } from "react"
import { useMutation } from "urql"

import type { GrCostBreakdownUpsertInput, Mutation } from "@/graphql/codegen/graphql"
import { UpsertGrCostBreakdownDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGrCostBreakdownMutationOptions {
  successMessage?: string
  errorMessage?: string
}

interface UseGrCostBreakdownMutationReturn {
  upsertGrCostBreakdown: (input: GrCostBreakdownUpsertInput) => Promise<boolean>
  isLoading: boolean
  error: any
}

export const useGrCostBreakdownMutation = (
  options?: UseGrCostBreakdownMutationOptions
): UseGrCostBreakdownMutationReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    Pick<Mutation, "grCostBreakdown">,
    { input: GrCostBreakdownUpsertInput }
  >(UpsertGrCostBreakdownDocument)

  const upsertGrCostBreakdown = useCallback(
    async (input: GrCostBreakdownUpsertInput): Promise<boolean> => {
      try {
        const result = await executeMutation({ input })

        if (result.error) {
          notify({
            type: NotificationType.Error,
            silent: false,
            message: options?.errorMessage || "Failed to save cost breakdown. Please try again.",
          })
          return false
        }

        // Check for GraphQL errors in the result
        const operation = result.data?.grCostBreakdown?.upsert
        if (operation?.__typename === "GrCostBreakdownUpsertFailure") {
          notify({
            type: NotificationType.Error,
            silent: false,
            message:
              operation.error?.message || options?.errorMessage || "Failed to save cost breakdown. Please try again.",
          })
          return false
        }

        notify({
          type: NotificationType.Success,
          silent: false,
          content: options?.successMessage || "Cost breakdown has been successfully saved",
        })

        return true
      } catch {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: options?.errorMessage || "An unexpected error occurred. Please try again.",
        })
        return false
      }
    },
    [executeMutation, notify, options?.errorMessage, options?.successMessage]
  )

  return {
    upsertGrCostBreakdown,
    isLoading: fetching,
    error,
  }
}
